import React, { useState, useEffect } from 'react';
import {
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Eye,
  RefreshCw,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Trash2,
} from 'lucide-react';
import { getAllVendors, getVendorStats, reattemptSharePointSync, deleteVendorFromFirebase } from '../../firebaseService';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../AuthProvider';

interface Vendor {
  id: string;
  companyName: string;
  email: string;
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: any;
  categories: Record<string, boolean>;
  registeredAddress: string;
  telephone: string;
  sharePointSyncStatus?: 'synced' | 'failed' | 'pending' | 'not-applicable';
  sharePointSyncError?: string;
  sharePointSyncDate?: any;
  sharePointSyncBy?: string;
}

const VendorListPage: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [resyncingVendors, setResyncingVendors] = useState<Set<string>>(new Set());
  const [deletingVendors, setDeletingVendors] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState<Vendor | null>(null);
  
  // Bulk selection state
  const [selectedVendors, setSelectedVendors] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const navigate = useNavigate();
  const { user } = useAuth();
  const { instance, accounts } = useMsal();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vendorsResult, statsResult] = await Promise.all([
        getAllVendors(),
        getVendorStats()
      ]);

      if (vendorsResult.success && vendorsResult.vendors) {
        setVendors(vendorsResult.vendors as Vendor[]);
      }

      if (statsResult.success && 'stats' in statsResult && statsResult.stats) {
        setCategoryOptions(Object.keys(statsResult.stats.categories));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVendors = vendors.filter(vendor => {
    const companyName = (vendor.companyName || '').toLowerCase();
    const email = (vendor.email || '').toLowerCase();
    const search = searchTerm.toLowerCase();

    const matchesSearch =
      !search ||
      companyName.includes(search) ||
      email.includes(search);
    const matchesStatus = statusFilter === 'all' || vendor.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || 
                           (vendor.categories && vendor.categories[categoryFilter]);

    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentVendors = filteredVendors.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, categoryFilter]);

  // Pagination functions
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages around current page
      const start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getCategoryName = (category: string) => {
    return category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const handleReSyncToSharePoint = async (vendorId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!accounts[0]) {
      alert('Please log in to re-sync to SharePoint');
      return;
    }

    setResyncingVendors(prev => new Set(prev).add(vendorId));

    try {
      // Get Microsoft Graph access token
      const tokenRequest = {
        scopes: ['Sites.ReadWrite.All', 'Files.ReadWrite.All', 'Sites.FullControl.All'],
        account: accounts[0]
      };

      const tokenResponse = await instance.acquireTokenSilent(tokenRequest);
      const accessToken = tokenResponse.accessToken;
      const approvedBy = accounts[0].name || accounts[0].username || 'System';

      const result = await reattemptSharePointSync(vendorId, accessToken, approvedBy);
      
      if (result.success) {
        alert('Vendor successfully re-synced to SharePoint!');
        // Refresh the data to update the sync status
        await fetchData();
      } else {
        alert(`Failed to re-sync vendor to SharePoint: ${result.error}`);
      }
    } catch (error) {
      console.error('Error re-syncing vendor:', error);
      alert(`Error re-syncing vendor: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setResyncingVendors(prev => {
        const newSet = new Set(prev);
        newSet.delete(vendorId);
        return newSet;
      });
    }
  };

  const getSharePointSyncStatus = (vendor: Vendor) => {
    if (vendor.status !== 'approved') {
      return 'not-applicable';
    }
    return vendor.sharePointSyncStatus || 'pending';
  };

  const handleDeleteVendor = (vendor: Vendor, e: React.MouseEvent) => {
    e.stopPropagation();
    setVendorToDelete(vendor);
    setShowDeleteModal(true);
  };

  const confirmDeleteVendor = async () => {
    if (!vendorToDelete) return;

    const vendorId = vendorToDelete.id;
    setDeletingVendors(prev => new Set(prev).add(vendorId));

    try {
      await deleteVendorFromFirebase(
        vendorId,
        user?.username || user?.email,
        user?.name || user?.username
      );
      alert('Vendor deleted successfully!');
      // Refresh the data
      await fetchData();
    } catch (error) {
      console.error('Error deleting vendor:', error);
      alert(`Error deleting vendor: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setDeletingVendors(prev => {
        const newSet = new Set(prev);
        newSet.delete(vendorId);
        return newSet;
      });
      setShowDeleteModal(false);
      setVendorToDelete(null);
    }
  };

  const cancelDeleteVendor = () => {
    setShowDeleteModal(false);
    setVendorToDelete(null);
  };

  // Bulk selection functions
  const handleSelectVendor = (vendorId: string, checked: boolean) => {
    setSelectedVendors(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(vendorId);
      } else {
        newSet.delete(vendorId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedVendors(new Set(currentVendors.map(vendor => vendor.id)));
    } else {
      setSelectedVendors(new Set());
    }
  };

  const handleBulkDelete = () => {
    if (selectedVendors.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedVendors.size === 0) return;

    setBulkDeleting(true);
    const vendorIds = Array.from(selectedVendors);
    
    try {
      // Delete vendors one by one
      for (const vendorId of vendorIds) {
        await deleteVendorFromFirebase(
        vendorId,
        user?.username || user?.email,
        user?.name || user?.username
      );
        console.log(`Deleted vendor: ${vendorId}`);
      }
      
      alert(`Successfully deleted ${vendorIds.length} vendor(s)!`);
      // Clear selection and refresh data
      setSelectedVendors(new Set());
      await fetchData();
    } catch (error) {
      console.error('Error bulk deleting vendors:', error);
      alert(`Error deleting vendors: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setBulkDeleting(false);
      setShowBulkDeleteModal(false);
    }
  };

  const cancelBulkDelete = () => {
    setShowBulkDeleteModal(false);
  };

  const getSharePointSyncIcon = (status: string) => {
    switch (status) {
      case 'synced':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'not-applicable':
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getSharePointSyncText = (status: string) => {
    switch (status) {
      case 'synced':
        return 'Synced';
      case 'failed':
        return 'Failed';
      case 'pending':
        return 'Pending';
      case 'not-applicable':
        return 'N/A';
      default:
        return 'Unknown';
    }
  };

  const getSharePointSyncColor = (status: string) => {
    switch (status) {
      case 'synced':
        return 'text-green-600 bg-green-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'not-applicable':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-yellow-600 bg-yellow-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sunbeth-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">Search, filter, and manage vendor applications.</p>
        {/* Filters and Search */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search vendors..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunbeth-orange-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex gap-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunbeth-orange-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>

                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunbeth-orange-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>
                      {getCategoryName(category)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Vendors Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Vendors ({filteredVendors.length})
              </h3>
              {filteredVendors.length > 0 && (
                <div className="text-sm text-gray-500">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredVendors.length)} of {filteredVendors.length}
                </div>
              )}
            </div>
          </div>
          
          {/* Bulk Actions */}
          {selectedVendors.size > 0 && (
            <div className="mb-4 p-4 bg-sunbeth-orange-50 border border-sunbeth-orange-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-sunbeth-orange-800">
                    {selectedVendors.size} vendor(s) selected
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 flex items-center"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Selected
                  </button>
                  <button
                    onClick={() => setSelectedVendors(new Set())}
                    className="px-4 py-2 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400"
                  >
                    Clear Selection
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedVendors.size > 0 && selectedVendors.size === currentVendors.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-sunbeth-orange-600 focus:ring-sunbeth-orange-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SharePoint Sync
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categories
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentVendors.map((vendor) => (
                  <tr 
                    key={vendor.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => {
                      // Only navigate if not clicking on interactive elements
                      const target = e.target as HTMLElement;
                      if (!target.closest('input[type="checkbox"]') && 
                          !target.closest('button') && 
                          !target.closest('a')) {
                        navigate(`/staff/vendor/${vendor.id}`);
                        // navigate(`/vendor/${vendor.id}`);
                      }
                    }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedVendors.has(vendor.id)}
                        onChange={(e) => {
                          handleSelectVendor(vendor.id, e.target.checked);
                        }}
                        className="rounded border-gray-300 text-sunbeth-orange-600 focus:ring-sunbeth-orange-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      <div>
                        <div className="text-sm font-medium text-gray-900 text-left">
                          {vendor.companyName}
                        </div>
                        <div className="text-sm text-gray-500 text-left">
                          {vendor.registeredAddress}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{vendor.email}</div>
                      <div className="text-sm text-gray-500">{vendor.telephone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vendor.status)}`}>
                        {getStatusIcon(vendor.status)}
                        <span className="ml-1 capitalize">{vendor.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSharePointSyncColor(getSharePointSyncStatus(vendor))}`}>
                          {getSharePointSyncIcon(getSharePointSyncStatus(vendor))}
                          <span className="ml-1">{getSharePointSyncText(getSharePointSyncStatus(vendor))}</span>
                        </span>
                        {vendor.status === 'approved' && getSharePointSyncStatus(vendor) !== 'synced' && (
                          <button
                            onClick={(e) => handleReSyncToSharePoint(vendor.id, e)}
                            disabled={resyncingVendors.has(vendor.id)}
                            className="text-sunbeth-orange-600 hover:text-sunbeth-orange-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                            title="Re-sync to SharePoint"
                          >
                            {resyncingVendors.has(vendor.id) ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {vendor.categories && Object.entries(vendor.categories)
                          .filter(([_, selected]) => selected)
                          .slice(0, 2)
                          .map(([category, _]) => getCategoryName(category))
                          .join(', ')}
                        {vendor.categories && Object.values(vendor.categories).filter(Boolean).length > 2 && 
                          ` +${Object.values(vendor.categories).filter(Boolean).length - 2} more`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(vendor.submittedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/staff/vendor/${vendor.id}`);
                            // navigate(`/vendor/${vendor.id}`);
                          }}
                          className="text-sunbeth-orange-600 hover:text-sunbeth-orange-900 flex items-center"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                        <button
                          onClick={(e) => handleDeleteVendor(vendor, e)}
                          disabled={deletingVendors.has(vendor.id)}
                          className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center"
                          title="Delete vendor"
                        >
                          {deletingVendors.has(vendor.id) ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredVendors.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No vendors found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by registering a new vendor.'}
              </p>
            </div>
          )}

          {/* Pagination Controls */}
          {filteredVendors.length > itemsPerPage && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(endIndex, filteredVendors.length)}</span> of{' '}
                    <span className="font-medium">{filteredVendors.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                    </button>
                    
                    {getPageNumbers().map((pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => goToPage(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNumber === currentPage
                            ? 'z-10 bg-sunbeth-orange-50 border-sunbeth-orange-500 text-sunbeth-orange-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ))}
                    
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && vendorToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Delete Vendor</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete <strong>{vendorToDelete.companyName}</strong>? 
                  This action cannot be undone and will permanently remove all vendor data and documents.
                </p>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={cancelDeleteVendor}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteVendor}
                  disabled={deletingVendors.has(vendorToDelete.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingVendors.has(vendorToDelete.id) ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Delete Multiple Vendors</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete <strong>{selectedVendors.size} vendor(s)</strong>? 
                  This action cannot be undone and will permanently remove all vendor data and documents.
                </p>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={cancelBulkDelete}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmBulkDelete}
                  disabled={bulkDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkDeleting ? 'Deleting...' : `Delete ${selectedVendors.size} Vendor(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorListPage;